/**
 * Tools that make, copy, remove, nest, name and select objects. A page of a
 * deck goes through the deck's own page actions, which keep its positional
 * `pages` in step; a copy of a deck's shared child is listed in `shared` with
 * it, so it does not read as a new page.
 */

import type { SceneObject } from '../../core/SceneObject';
import { fontSizeUnitWarning } from '../../creator-tools/build/unit-guards';
import { OBJECT_TEMPLATES, type ObjectTemplate, type TemplateDescriptor } from '../create/templates';
import type { UndoCommand } from '../undo/UndoCommand';
import { editRefusal } from '../authoring/checks';
import { topLevelParent } from '../authoring/documentRoot';
import { createObject, deleteObjects, duplicateObjects, setActive, setParent } from '../authoring/objectOps';
import { bundle, rollBack } from '../authoring/steps';
import { addPage, deletePage, duplicatePage, renameObject } from '../pages/pageActions';
import { pageChildrenOf } from '../pages/pageContext';
import { pagingOf, pruneSharedEntryForDelete, registerSharedChild } from '../pages/sharedChildren';
import { setTransforms } from '../authoring/transformOps';
import { setMaterialColors } from '../authoring/colorOps';
import { readOnlyRefusal } from '../authoring/referenceEdit';
import { normalizeAuthoredText } from '../authoredText';
import {
    POSITION_ANCHORS, VEC3, defineTool, fail, keep, positionAgainst, readHexColor, readTransformArgs, refusalText, resolveRef, restAgainst, whereText, type PositionAnchor, type Vec3,
} from './input';
import type { Refusal } from '../authoring/types';
import type { SceneTool, SceneToolHost } from './types';

/** The empty template with a particle system. */
const PARTICLE_EMITTER: TemplateDescriptor = {
    ...OBJECT_TEMPLATES.empty,
    label: 'Particle emitter',
    defaultName: 'ParticleEmitter',
    behaviors: ['ParticleSystem'],
};

/** A deck: an empty with Pages, made with its first page. */
const PAGES: TemplateDescriptor = {
    ...OBJECT_TEMPLATES.empty,
    label: 'Pages',
    defaultName: 'Pages',
    behaviors: ['Pages'],
};

/** The templates `create_object` makes, by the name a call gives. */
const CREATE_TEMPLATES: Readonly<Record<string, ObjectTemplate | TemplateDescriptor>> = {
    empty: 'empty',
    cube: 'cube',
    sphere: 'sphere',
    plane: 'plane',
    cylinder: 'cylinder',
    text: 'text',
    pages: PAGES,
    'particle-emitter': PARTICLE_EMITTER,
};

/** The deck `object` is a page of, and its index there; null when it is no page. */
function pageOf(object: SceneObject): { container: SceneObject; index: number } | null {
    const container = object.getParent();
    if (!container || !pagingOf(container)) return null;
    const index = pageChildrenOf(container).indexOf(object);
    return index >= 0 ? { container, index } : null;
}

/** Whether `object` is a direct child of a deck that lists it in `shared`. */
function isSharedChild(object: SceneObject): boolean {
    const container = object.getParent();
    const pages = container ? pagingOf(container) : null;
    if (!container || !pages) return false;
    const path = object.traceScene.getRelativePath(object, container);
    return !!path && (pages.getData().shared ?? []).includes(path);
}

/** Where `object` stands as a page, for an answer: "" when it is no page. */
function pageNote(object: SceneObject): string {
    const page = pageOf(object);
    return page ? ` It is page ${page.index + 1} of the deck "${page.container.name}".` : '';
}

/**
 * Run `op` on `objects` after dropping what a deck stores about each as shared
 * content (`pruneSharedEntryForDelete`), as one step: a path left behind would
 * name whatever lands there next. Every deck edit is taken back when `op`
 * refuses or faults.
 */
async function withSharedEntryPruned<T extends { command: UndoCommand }>(
    host: SceneToolHost,
    list: readonly SceneObject[],
    op: () => Promise<T | { refused: Refusal }>,
): Promise<T | { refused: Refusal }> {
    const refusal = editRefusal(host.scene, list);
    if (refusal) return { refused: refusal };
    const pruned: UndoCommand[] = [];
    let result: T | { refused: Refusal };
    try {
        for (const object of list) {
            const step = await pruneSharedEntryForDelete(object);
            if (step) pruned.push(step);
        }
        result = await op();
    } catch (error) {
        await rollBack(pruned, error);
        throw error;
    }
    if ('refused' in result) {
        await rollBack(pruned, null);
        return result;
    }
    return pruned.length ? { ...result, command: bundle(result.command.label, [...pruned, result.command]) } : result;
}

const createTool = defineTool({
    name: 'create_object',
    verb: 'Adding',
    edits: true,
    additive: true,
    inputSchema: {
        type: 'object',
        properties: {
            template: { type: 'string', enum: Object.keys(CREATE_TEMPLATES) },
            name: { type: 'string' },
            parent: { type: 'string' },
            position: VEC3,
            position_relative: {
                type: 'object',
                properties: {
                    target: { type: 'string' },
                    anchor: { type: 'string', enum: [...POSITION_ANCHORS] },
                    offset: { type: 'number' },
                },
                required: ['anchor'],
            },
            rotation: VEC3,
            scale: VEC3,
            active: { type: 'boolean' },
            text: { type: 'string' },
            color: { type: 'string' },
            fontSize: { type: 'number' },
        },
        required: ['template'],
    },
    async run(input, host) {
        const template = input.template;
        const name = typeof input.name === 'string' ? input.name : undefined;
        const transform = readTransformArgs(input);
        if ('error' in transform) return fail(transform.error);
        const { rotation, scale } = transform;
        let { position } = transform;
        const active = input.active;
        if (active !== undefined && typeof active !== 'boolean') return fail(`Error: active must be true or false, not ${JSON.stringify(active)}.`);
        if (typeof template !== 'string' || !Object.hasOwn(CREATE_TEMPLATES, template)) {
            return fail(`Error: Invalid template "${String(template)}". Valid templates: ${Object.keys(CREATE_TEMPLATES).join(', ')}`);
        }

        // "At the top" is the document root, never the engine's mount point.
        let parent = topLevelParent(host.scene);
        if (typeof input.parent === 'string' && input.parent) {
            const found = resolveRef(host, input.parent);
            if (!found) return fail(`Error: Parent object "${input.parent}" not found.`);
            parent = found;
        }

        // An absolute position wins over a relative one.
        const relative = input.position_relative as { target?: unknown; anchor?: unknown; offset?: unknown } | undefined;
        let restOn: { target: SceneObject; anchor: PositionAnchor; offset: number } | null = null;
        if (relative && !position) {
            const targetRef = typeof relative.target === 'string' ? relative.target : parent?.name;
            if (!targetRef) {
                return fail('Error: position_relative requires an explicit "target" when creating at the scene root (no parent to default to).');
            }
            const target = resolveRef(host, targetRef);
            const anchor = POSITION_ANCHORS.includes(relative.anchor as PositionAnchor) ? relative.anchor as PositionAnchor : null;
            const at = target && anchor ? positionAgainst(target, anchor, typeof relative.offset === 'number' ? relative.offset : 0, parent) : null;
            if (!at) return fail(`Error: position_relative target "${targetRef}" not found or has no bounds.`);
            position = at;
            restOn = { target: target!, anchor: anchor!, offset: typeof relative.offset === 'number' ? relative.offset : 0 };
        }

        // A primitive's color is its material's; a text's is its Text's (below).
        const primitive = typeof CREATE_TEMPLATES[template] === 'string' && !!OBJECT_TEMPLATES[CREATE_TEMPLATES[template]]?.primitive;
        // null or "" means no color, as an empty pose field means leave it alone.
        const given = input.color !== undefined && input.color !== null && input.color !== '';
        const paint = primitive && given ? readHexColor(input.color) : null;
        if (primitive && given && !paint) return fail(`Error: color must be #RRGGBB or #RRGGBBAA, not ${JSON.stringify(input.color)}.`);
        const text: Record<string, unknown> = {};
        if (template === 'text') {
            if (input.text) text.text = typeof input.text === 'string' ? normalizeAuthoredText(input.text) : input.text;
            if (input.color) text.color = input.color;
            if (input.fontSize) text.fontSize = input.fontSize;
        }
        // Directly in a deck, it is the deck's last page, shown only when it is the first, so the page showing stays the one that shows.
        const asPage = parent && pagingOf(parent) ? { active: pageChildrenOf(parent).length === 0 } : {};
        const created = await createObject(host.scene, CREATE_TEMPLATES[template], {
            parent,
            name,
            position,
            rotation,
            scale,
            ...asPage,
            ...(active !== undefined ? { active } : {}),
            ...(Object.keys(text).length > 0 ? { behaviorData: { Text: text } } : {}),
        });
        if ('refused' in created) return fail(`Error creating object: ${refusalText(host, created.refused)}`);
        const object = created.object;
        let command = created.command;
        // Placed against another object: its near side rests on the face, not its origin.
        const rest = restOn ? restAgainst(object, restOn.target, restOn.anchor, restOn.offset) : null;
        let restNote = '';
        if (rest) {
            let moved: Awaited<ReturnType<typeof setTransforms>>;
            try {
                moved = await setTransforms([{ object, position: rest }], host.ports());
            } catch (error) {
                await rollBack([command], error);
                throw error;
            }
            const [refused] = moved.refused;
            if (refused) restNote = ` It could not be rested against "${restOn!.target.name}", so its origin is on that face: ${refusalText(host, refused)}`;
            else if (moved.command) command = bundle(`Create ${object.name}`, [command, moved.command]);
        } else if (restOn) {
            restNote = ` It draws nothing to rest by, so its origin is on "${restOn.target.name}"'s face.`;
        }
        if (paint) {
            let painted: Awaited<ReturnType<typeof setMaterialColors>>;
            try {
                painted = await setMaterialColors([{ object, color: paint }], host.ports());
            } catch (error) {
                await rollBack([command], error);
                throw error;
            }
            const [refused] = painted.refused;
            if (refused) {
                await rollBack([command], null);
                return fail(`Error creating object: its color was refused: ${refusalText(host, refused)}`);
            }
            if (painted.command) command = bundle(`Create ${object.name}`, [command, painted.command]);
        }
        if (template === 'pages') {
            let first: Awaited<ReturnType<typeof addPage>>;
            try {
                first = await addPage(object);
            } catch (error) {
                await rollBack([command], error);
                throw error;
            }
            if (!first) {
                await rollBack([command], null);
                return fail(`Error creating object: the deck "${object.name}" could not take its first page.`);
            }
            command = bundle(`Create ${object.name}`, [command, first.command]);
        }
        host.record(command);
        host.select?.(object);
        host.changed?.();

        const at = `${whereText(object)}${restNote}${pageNote(object)}`;
        if (template === 'pages') return { text: `Created pages container "${object.name}" with initial page.${at}` };
        if (template === 'particle-emitter') return { text: `Created particle emitter "${object.name}".${at}` };
        if (template === 'text') {
            const warning = fontSizeUnitWarning(input.fontSize);
            return { text: `Created text object "${object.name}"${typeof input.text === 'string' && input.text ? ` with text "${input.text}"` : ''}.${warning ? ` ${warning}` : ''}${at}` };
        }
        return { text: `Created ${template} object "${object.name}".${at}` };
    },
});

/** One copy of `source` (not a page) with the name and pose given, a deck's shared child listed with it: the copy and its step, not recorded. */
async function duplicateOne(
    host: SceneToolHost, source: SceneObject, name: string | undefined, pose: { position?: Vec3; rotation?: Vec3; scale?: Vec3 },
): Promise<{ copy: SceneObject; command: UndoCommand } | { error: string }> {
    const result = await duplicateObjects(source, { name, ...pose });
    if ('refused' in result) return { error: `Error duplicating object: ${refusalText(host, result.refused)}` };
    const copy = result.copies.get(source)!;
    let command = result.command;
    const container = source.getParent();
    if (container && isSharedChild(source)) {
        let listed: UndoCommand | null;
        try {
            listed = await registerSharedChild(container, copy);
        } catch (error) {
            await rollBack([result.command], error);
            throw error;
        }
        if (listed) command = bundle(`Duplicate ${copy.name}`, [command, listed]);
    }
    return { copy, command };
}

/** The most objects one delete call takes: a sentence's worth, not a scene's. */
const MAX_DELETES = 100;

/** The most copies one call makes: enough for any layout a user asks for in a sentence. */
const MAX_COPIES = 50;

const POSE_ENTRY = { type: 'object', properties: { name: { type: 'string' }, position: VEC3, rotation: VEC3, scale: VEC3 } } as const;

const duplicateTool = defineTool({
    name: 'duplicate_object',
    verb: 'Duplicating',
    edits: true,
    additive: true,
    inputSchema: {
        type: 'object',
        properties: { object: { type: 'string' }, name: { type: 'string' }, position: VEC3, rotation: VEC3, scale: VEC3, copies: { type: 'array', items: POSE_ENTRY } },
        required: ['object'],
    },
    async run(input, host) {
        const transform = readTransformArgs(input);
        if ('error' in transform) return fail(transform.error);
        const source = resolveRef(host, input.object);
        if (!source) return fail(`Error: Object "${String(input.object)}" not found.`);
        const descendants = source.getDescendants().length;
        const descendantNote = descendants > 0 ? ` (with ${descendants} descendant${descendants > 1 ? 's' : ''})` : '';
        const copies = input.copies;
        if (copies !== undefined && (!Array.isArray(copies) || copies.length === 0)) return fail('Error: copies is a non-empty list of {name, position, rotation, scale}.');
        if (Array.isArray(copies) && copies.length > MAX_COPIES) return fail(`Error: at most ${MAX_COPIES} copies in one call.`);
        if (copies !== undefined && (typeof input.name === 'string' || Object.keys(transform).length > 0)) {
            return fail('Error: give name and pose per entry in copies, or for one copy at the top level, not both.');
        }

        const page = pageOf(source);
        if (page) {
            if (copies !== undefined) return fail(`Error: "${source.name}" is a page; a page is copied one at a time, into the next place in its deck.`);
            const result = await duplicatePage(page.container, page.index);
            if (!result) return fail(`Error duplicating object: the deck "${page.container.name}" cannot be edited now.`);
            if ('refused' in result) return fail(`Error duplicating object: ${refusalText(host, result.refused)}`);
            host.record(result.command);
            host.select?.(result.copy);
            host.changed?.();
            const ignored = typeof input.name === 'string' || Object.keys(transform).length > 0
                ? ' A page copy takes the next place in the deck, so the name and pose given were not applied.' : '';
            return { text: `Duplicated page "${source.name}" as "${result.copy.name}"${descendantNote}.${ignored}` };
        }

        // Each entry of copies is one copy; without it, the call makes one. All of them are one undo step.
        const poses: { name?: string; pose: { position?: Vec3; rotation?: Vec3; scale?: Vec3 } }[] = [];
        if (!copies) poses.push({ name: typeof input.name === 'string' ? input.name : undefined, pose: transform });
        for (const [index, entry] of ((copies as unknown[] | undefined) ?? []).entries()) {
            if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return fail(`Error: copies[${index}] must be {name, position, rotation, scale}.`);
            const fields = entry as Record<string, unknown>;
            const unknown = Object.keys(fields).filter(key => !['name', 'position', 'rotation', 'scale'].includes(key));
            if (unknown.length) return fail(`Error: copies[${index}] has no ${unknown.map(k => `"${k}"`).join(', ')}; an entry is {name, position, rotation, scale}.`);
            const pose = readTransformArgs(fields);
            if ('error' in pose) return fail(`Error: copies[${index}]: ${pose.error}`);
            poses.push({ name: typeof fields.name === 'string' ? fields.name : undefined, pose });
        }
        const made: SceneObject[] = [];
        const steps: UndoCommand[] = [];
        try {
            for (const { name, pose } of poses) {
                const one = await duplicateOne(host, source, name, pose);
                if ('error' in one) {
                    await rollBack(steps, null);
                    return fail(copies ? `${one.error} No copies were kept.` : one.error);
                }
                made.push(one.copy);
                steps.push(one.command);
            }
        } catch (error) {
            await rollBack(steps, error);
            throw error;
        }
        host.record(steps.length === 1 ? steps[0] : bundle(`Duplicate ${source.name} ×${steps.length}`, steps));
        host.select?.(made[made.length - 1]);
        host.changed?.();
        if (!copies) return { text: `Duplicated "${source.name}" as "${made[0].name}"${descendantNote}.${whereText(made[0])}` };
        return { text: [`Duplicated "${source.name}" ${made.length} times${descendantNote}.`, ...made.map(copy => `  "${copy.name}":${whereText(copy)}`)].join('\n') };
    },
});

const deleteTool = defineTool({
    name: 'delete_object',
    verb: 'Deleting',
    edits: true,
    inputSchema: { type: 'object', properties: { object: { type: 'string' }, objects: { type: 'array', items: { type: 'string' } } } },
    async run(input, host) {
        if (input.objects !== undefined && input.object !== undefined) return fail('Error: give `object` or `objects`, not both.');
        const refs: unknown[] = Array.isArray(input.objects) ? input.objects : input.object !== undefined ? [input.object] : [];
        if (refs.length === 0) return fail('Error: delete_object needs `object`, or `objects` to delete several.');
        if (refs.length > MAX_DELETES) return fail(`Error: at most ${MAX_DELETES} objects in one call.`);
        const targets: SceneObject[] = [];
        for (const ref of refs) {
            const target = resolveRef(host, ref);
            if (!target) return fail(`Error: Object "${String(ref)}" not found.${refs.length > 1 ? ' Nothing was deleted.' : ''}`);
            if (!targets.includes(target)) targets.push(target);
        }
        // Something inside another listed object goes with it, so only the outermost are deleted (and checked).
        const outermost = targets.filter(t => !targets.some(other => other !== t && t.getPath().startsWith(other.getPath() + '/')));
        targets.splice(0, targets.length, ...outermost);
        const selected = host.selected?.() ?? null;
        const wasSelected = !!selected && targets.some(t => t === selected || selected.getPath().startsWith(t.getPath() + '/'));
        const descendants = (target: SceneObject): string => {
            const n = target.getDescendants().length;
            return n > 0 ? ` (and ${n} descendant${n > 1 ? 's' : ''})` : '';
        };

        const page = targets.length === 1 ? pageOf(targets[0]) : null;
        if (targets.length > 1 && targets.some(t => pageOf(t))) {
            return fail(`Error: "${targets.find(t => pageOf(t))!.name}" is a page; pages are deleted one at a time, through their deck. Nothing was deleted.`);
        }
        const lines = targets.map(t => `"${t.name}"${descendants(t)}`);
        if (page) {
            const result = await deletePage(page.container, page.index, host.ports());
            if (!result) return fail(`Error: the deck "${page.container.name}" cannot be edited now.`);
            if ('refused' in result) return fail(`Error: ${refusalText(host, result.refused)}`);
            host.record(result.command);
        } else {
            // All of them in one step: deleting a group and something inside it deletes the group once.
            const result = await withSharedEntryPruned(host, targets, () => deleteObjects(targets, host.ports()));
            if ('refused' in result) return fail(`Error: ${refusalText(host, result.refused)}${targets.length > 1 ? ' Nothing was deleted.' : ''}`);
            keep(host, 'delete_object', result);
        }
        if (wasSelected) host.select?.(null);
        host.changed?.();
        return { text: `Deleted ${page ? 'page ' : ''}${lines.join(', ')}.` };
    },
});

/** Move `target` under `parent` (or to the top, with `toRoot`): its result line, and the step it applied (not recorded). */
async function reparent(
    host: SceneToolHost, target: SceneObject, parent: SceneObject, toRoot: boolean,
): Promise<{ text: string; command: UndoCommand | null; orphaned?: readonly string[] } | { error: string }> {
    const label = toRoot ? `the scene root "${parent.name}"` : `"${parent.name}"`;
    if (target.getParent() === parent) return { text: `Set parent of "${target.name}" to ${label}.`, command: null };

    const deck = pageOf(target)?.container;
    if (deck) {
        return { error: `Error: "${target.name}" is a page of the deck "${deck.name}", and a page stays in its deck. Move what is on it instead, or use duplicate_object or delete_object on the page.` };
    }

    const oldName = target.name;
    const result = await withSharedEntryPruned(host, [target], () => setParent(target, parent, host.ports()));
    if ('refused' in result) {
        if (result.refused.code === 'REPARENT_REFUSED' && result.refused.object === parent) {
            return { error: `Error: Cannot parent "${target.name}" under "${parent.name}": it is (or is inside) an imported scene reference, whose children are derived and would be lost on export. Choose a parent outside the imported subtree, e.g. a sibling group object.` };
        }
        return { error: `Error: ${refusalText(host, result.refused)}` };
    }
    // Directly in a deck, it is the deck's last page, hidden so the page showing stays the one that shows.
    if (pageOf(target) && pageChildrenOf(parent).length > 1 && target.active) {
        let hidden: Awaited<ReturnType<typeof setActive>>;
        try {
            hidden = await setActive([{ object: target, active: false }], host.ports());
        } catch (error) {
            await rollBack([result.command], error);
            throw error;
        }
        const [refused] = hidden.refused;
        if (refused) {
            await rollBack([result.command], null);
            return { error: `Error: "${target.name}" would be a page of "${parent.name}" but cannot be hidden behind the page showing, so it was not moved: ${refusalText(host, refused)}` };
        }
        if (hidden.command) result.command = bundle(result.command.label, [result.command, hidden.command]);
    }
    const renamed = target.name !== oldName ? ` (renamed to "${target.name}" to avoid sibling conflict)` : '';
    return { text: `Set parent of "${target.name}" to ${label}.${renamed}${pageNote(target)}`, command: result.command, orphaned: result.orphaned };
}

const setParentTool = defineTool({
    name: 'set_parent',
    verb: 'Moving',
    edits: true,
    idempotent: true,
    inputSchema: {
        type: 'object',
        properties: { object: { type: 'string' }, objects: { type: 'array', items: { type: 'string' } }, parent: { type: 'string' } },
    },
    async run(input, host) {
        if (input.objects !== undefined && input.object !== undefined) return fail('Error: give `object` or `objects`, not both.');
        const refs: unknown[] = Array.isArray(input.objects) ? input.objects : input.object !== undefined ? [input.object] : [];
        if (refs.length === 0) return fail('Error: set_parent needs `object`, or `objects` to move several under one parent.');
        // Omitted or "root": the top of the scene, which is the document root.
        const toRoot = !input.parent || input.parent === 'root';
        const parent = toRoot ? topLevelParent(host.scene) : resolveRef(host, input.parent);
        if (!parent) return fail(`Error: Parent object "${String(input.parent)}" not found.`);

        // One step for the call: each object moves keeping its world pose, in the order given.
        const lines: string[] = [];
        const steps: UndoCommand[] = [];
        const orphaned: string[] = [];
        try {
            for (const ref of refs) {
                const target = resolveRef(host, ref);
                if (!target) { lines.push(`Error: Object "${String(ref)}" not found.`); continue; }
                const moved = await reparent(host, target, parent, toRoot);
                if ('error' in moved) { lines.push(moved.error); continue; }
                lines.push(moved.text);
                if (moved.command) steps.push(moved.command);
                orphaned.push(...(moved.orphaned ?? []));
            }
        } catch (error) {
            // A fault part way: the moves already made are taken back, so nothing is left unrecorded.
            await rollBack(steps, error);
            throw error;
        }
        const moved = lines.filter(line => !line.startsWith('Error')).length;
        if (steps.length > 0) {
            keep(host, 'set_parent', { command: steps.length === 1 ? steps[0] : bundle(`Move ${steps.length} objects`, steps), orphaned });
            host.changed?.();
        }
        const text = lines.join('\n');
        return moved > 0 ? { text } : fail(text);
    },
});

const renameTool = defineTool({
    name: 'rename_object',
    verb: 'Renaming',
    edits: true,
    idempotent: true,
    inputSchema: { type: 'object', properties: { object: { type: 'string' }, name: { type: 'string' } }, required: ['object', 'name'] },
    async run(input, host) {
        const target = resolveRef(host, input.object);
        if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
        const name = typeof input.name === 'string' ? input.name.trim() : '';
        if (!name) return fail('Error: Provide the new name.');
        const refusal = editRefusal(host.scene, [target]) ?? readOnlyRefusal(target, host.ports());
        if (refusal) return fail(`Error: ${refusalText(host, refusal)}`);
        const oldName = target.name;
        if (name === oldName) return { text: `"${oldName}" already has that name.` };
        const command = await renameObject(target, name);
        if (!command) return fail(`Error: Could not rename "${oldName}" to "${name}": a name must be unique among its siblings and cannot contain "/".`);
        host.record(command);
        host.changed?.();
        const inside = target.getChildren().length > 0 ? ' The paths of the objects inside it changed too.' : '';
        return { text: `Renamed "${oldName}" to "${target.name}"; its path is now "${target.getPath()}".${inside}` };
    },
});

const selectTool = defineTool({
    name: 'select_object',
    verb: 'Selecting',
    edits: false,
    requires: ['select'],
    inputSchema: { type: 'object', properties: { object: { type: 'string' } } },
    run(input, host) {
        const ref = input.object;
        // An object actually named "none" wins: the word only clears when nothing has that name.
        const target = typeof ref === 'string' && ref ? resolveRef(host, ref) : null;
        // `requires` has checked the host can select.
        if (!target && (!ref || ref === 'none')) {
            host.select!(null);
            return Promise.resolve({ text: 'Selection cleared.' });
        }
        if (!target) return Promise.resolve(fail(`Error: Object "${String(ref)}" not found.`));
        host.select!(target);
        return Promise.resolve({ text: `Selected "${target.name}" (${target.getPath()}).` });
    },
});

export const OBJECT_TOOLS: readonly SceneTool[] = [createTool, duplicateTool, deleteTool, setParentTool, renameTool, selectTool];

